/**
 * Substrate-specific gamut mapping and out-of-gamut detection
 * Uses hybrid approach with substrate property transformations
 */

import Color from 'colorjs.io';
import chroma from 'chroma-js';
import type { CMYK } from '../colorEngine';
import type { SubstrateProfile } from '../../types/substrate';

// Cache for gamut boundaries to avoid recalculation
const gamutBoundaryCache = new Map<string, GamutBoundary>();

export interface GamutBoundary {
  labMin: { l: number; a: number; b: number };
  labMax: { l: number; a: number; b: number };
  cmykLimits: { cyan: number; magenta: number; yellow: number; black: number };
  tacLimit: number;
  brightness: number;
  absorptionFactors: { cyan: number; magenta: number; yellow: number; black: number };
}

export interface GamutAnalysisResult {
  inGamut: boolean;
  distance: number;
  nearestInGamutColor: {
    hex: string;
    lab: { l: number; a: number; b: number };
    cmyk: CMYK;
  };
  gamutVolume: number;
  compressionRatio: number;
}

export interface GamutCompressionOptions {
  method: 'clip' | 'compression' | 'adaptive';
  preserveHue?: boolean;
  compressionStrength?: number;
}

/**
 * Calculate substrate-specific gamut boundaries based on physical properties
 */
export function calculateSubstrateGamutBoundary(substrate: SubstrateProfile): GamutBoundary {
  // Check cache first
  if (gamutBoundaryCache.has(substrate.id)) {
    return gamutBoundaryCache.get(substrate.id)!;
  }

  // Base sRGB gamut boundaries in Lab space
  const sRGBGamut = {
    labMin: { l: 0, a: -128, b: -128 },
    labMax: { l: 100, a: 127, b: 127 }
  };

  // Adjust boundaries based on substrate brightness
  const brightnessFactor = substrate.brightness / 100;
  const lMin = sRGBGamut.labMin.l * brightnessFactor;
  const lMax = sRGBGamut.labMax.l * brightnessFactor;

  // Adjust chroma boundaries based on absorption coefficients
  const avgAbsorption = Object.values(substrate.absorptionCoefficients)
    .reduce((sum, coeff) => sum + coeff, 0) / 4;
  const chromaReduction = 1 - (avgAbsorption * 0.3); // Absorption reduces chroma
  
  const aMin = sRGBGamut.labMin.a * chromaReduction;
  const aMax = sRGBGamut.labMax.a * chromaReduction;
  const bMin = sRGBGamut.labMin.b * chromaReduction;
  const bMax = sRGBGamut.labMax.b * chromaReduction;

  const boundary = {
    labMin: { l: lMin, a: aMin, b: bMin },
    labMax: { l: lMax, a: aMax, b: bMax },
    cmykLimits: substrate.inkLimits,
    tacLimit: substrate.tacLimit,
    brightness: substrate.brightness,
    absorptionFactors: substrate.absorptionCoefficients
  };

  // Cache the result
  gamutBoundaryCache.set(substrate.id, boundary);
  
  return boundary;
}

/**
 * Check if a color is within substrate gamut
 */
export function isColorInSubstrateGamut(
  color: string,
  substrate: SubstrateProfile
): boolean {
  try {
    const colorObj = new Color(color);
    const lab = colorObj.to('lab');
    const boundary = calculateSubstrateGamutBoundary(substrate);

    return (
      lab.coords[0] >= boundary.labMin.l &&
      lab.coords[0] <= boundary.labMax.l &&
      lab.coords[1] >= boundary.labMin.a &&
      lab.coords[1] <= boundary.labMax.a &&
      lab.coords[2] >= boundary.labMin.b &&
      lab.coords[2] <= boundary.labMax.b
    );
  } catch {
    return false;
  }
}

/**
 * Calculate distance from color to substrate gamut boundary
 */
export function calculateGamutDistance(
  color: string,
  substrate: SubstrateProfile
): number {
  try {
    const colorObj = new Color(color);
    const lab = colorObj.to('lab');
    const boundary = calculateSubstrateGamutBoundary(substrate);

    // Calculate distance to nearest boundary
    let distance = 0;
    const [l, a, b] = lab.coords;

    // L distance
    if (l < boundary.labMin.l) {
      distance += Math.pow(boundary.labMin.l - l, 2);
    } else if (l > boundary.labMax.l) {
      distance += Math.pow(l - boundary.labMax.l, 2);
    }

    // A distance
    if (a < boundary.labMin.a) {
      distance += Math.pow(boundary.labMin.a - a, 2);
    } else if (a > boundary.labMax.a) {
      distance += Math.pow(a - boundary.labMax.a, 2);
    }

    // B distance
    if (b < boundary.labMin.b) {
      distance += Math.pow(boundary.labMin.b - b, 2);
    } else if (b > boundary.labMax.b) {
      distance += Math.pow(b - boundary.labMax.b, 2);
    }

    return Math.sqrt(distance);
  } catch {
    return Infinity;
  }
}

/**
 * Find nearest in-gamut color for out-of-gamut colors
 */
export function findNearestInGamutColor(
  color: string,
  substrate: SubstrateProfile,
  options: GamutCompressionOptions = { method: 'clip' }
): {
  hex: string;
  lab: { l: number; a: number; b: number };
  cmyk: CMYK;
} {
  try {
    const colorObj = new Color(color);
    const lab = colorObj.to('lab');
    const boundary = calculateSubstrateGamutBoundary(substrate);

    let [l, a, b] = lab.coords;

    switch (options.method) {
      case 'clip':
        // Simple clipping to gamut boundaries
        l = Math.max(boundary.labMin.l, Math.min(boundary.labMax.l, l));
        a = Math.max(boundary.labMin.a, Math.min(boundary.labMax.a, a));
        b = Math.max(boundary.labMin.b, Math.min(boundary.labMax.b, b));
        break;

      case 'compression':
        // Compress towards center of gamut
        {
          const centerL = (boundary.labMin.l + boundary.labMax.l) / 2;
          const centerA = (boundary.labMin.a + boundary.labMax.a) / 2;
          const centerB = (boundary.labMin.b + boundary.labMax.b) / 2;

          const compressionStrength = options.compressionStrength ?? 0.8;

          l = centerL + (l - centerL) * compressionStrength;
          a = centerA + (a - centerA) * compressionStrength;
          b = centerB + (b - centerB) * compressionStrength;

          // Then clip to ensure within bounds
          l = Math.max(boundary.labMin.l, Math.min(boundary.labMax.l, l));
          a = Math.max(boundary.labMin.a, Math.min(boundary.labMax.a, a));
          b = Math.max(boundary.labMin.b, Math.min(boundary.labMax.b, b));
        }
        break;

      case 'adaptive':
        // Adaptive compression based on distance from boundary
        {
          const distance = calculateGamutDistance(color, substrate);
          const adaptiveStrength = Math.max(0.5, 1 - (distance / 100));

          const adaptiveCenterL = (boundary.labMin.l + boundary.labMax.l) / 2;
          const adaptiveCenterA = (boundary.labMin.a + boundary.labMax.a) / 2;
          const adaptiveCenterB = (boundary.labMin.b + boundary.labMax.b) / 2;

          l = adaptiveCenterL + (l - adaptiveCenterL) * adaptiveStrength;
          a = adaptiveCenterA + (a - adaptiveCenterA) * adaptiveStrength;
          b = adaptiveCenterB + (b - adaptiveCenterB) * adaptiveStrength;

          // Clip to ensure within bounds
          l = Math.max(boundary.labMin.l, Math.min(boundary.labMax.l, l));
          a = Math.max(boundary.labMin.a, Math.min(boundary.labMax.a, a));
          b = Math.max(boundary.labMin.b, Math.min(boundary.labMax.b, b));
        }
        break;
    }

    // Convert back to color and CMYK
    const correctedColor = new Color('lab', [l, a, b]);
    const srgbColor = correctedColor.to('srgb');

    // Convert to CMYK using chroma-js for accuracy
    const hex = srgbColor.toString({ format: 'hex' });
    const cmykValues = chroma(hex).cmyk();

    const cmyk: CMYK = {
      c: cmykValues[0] * 100,
      m: cmykValues[1] * 100,
      y: cmykValues[2] * 100,
      k: cmykValues[3] * 100
    };

    return {
      hex,
      lab: { l, a, b },
      cmyk
    };
  } catch {
    // Fallback to grayscale if conversion fails
    return {
      hex: '#808080',
      lab: { l: 50, a: 0, b: 0 },
      cmyk: { c: 0, m: 0, y: 0, k: 50 }
    };
  }
}

/**
 * Perform comprehensive gamut analysis
 */
export function analyzeGamut(
  color: string,
  substrate: SubstrateProfile,
  options: GamutCompressionOptions = { method: 'clip' }
): GamutAnalysisResult {
  const inGamut = isColorInSubstrateGamut(color, substrate);
  const distance = inGamut ? 0 : calculateGamutDistance(color, substrate);
  const nearestInGamutColor = findNearestInGamutColor(color, substrate, options);
  
  // Calculate approximate gamut volume
  const boundary = calculateSubstrateGamutBoundary(substrate);
  const gamutVolume = 
    (boundary.labMax.l - boundary.labMin.l) *
    (boundary.labMax.a - boundary.labMin.a) *
    (boundary.labMax.b - boundary.labMin.b);
  
  // Calculate compression ratio (how much the substrate reduces the gamut)
  const sRGBVolume = 100 * 256 * 256; // Full sRGB gamut volume
  const compressionRatio = gamutVolume / sRGBVolume;

  return {
    inGamut,
    distance,
    nearestInGamutColor,
    gamutVolume,
    compressionRatio
  };
}

/**
 * Generate gamut visualization data
 */
export function generateGamutVisualization(
  substrate: SubstrateProfile,
  resolution: number = 20
): {
  boundary: GamutBoundary;
  samplePoints: Array<{
    lab: { l: number; a: number; b: number };
    hex: string;
    inGamut: boolean;
  }>;
} {
  const boundary = calculateSubstrateGamutBoundary(substrate);
  const samplePoints: Array<{
    lab: { l: number; a: number; b: number };
    hex: string;
    inGamut: boolean;
  }> = [];

  // Generate sample points across the gamut
  for (let l = 0; l <= 100; l += 100 / resolution) {
    for (let a = -128; a <= 127; a += 256 / resolution) {
      for (let b = -128; b <= 127; b += 256 / resolution) {
        try {
          const color = new Color('lab', [l, a, b]);
          const srgb = color.to('srgb');
          const hex = srgb.toString({ format: 'hex' });
          
          samplePoints.push({
            lab: { l, a, b },
            hex,
            inGamut: isColorInSubstrateGamut(hex, substrate)
          });
        } catch {
          // Skip invalid colors
        }
      }
    }
  }

  return {
    boundary,
    samplePoints
  };
}

/**
 * Clear gamut boundary cache (useful for testing or when substrate profiles change)
 */
export function clearGamutBoundaryCache(): void {
  gamutBoundaryCache.clear();
}

/**
 * Get cache statistics
 */
export function getGamutCacheStats(): { size: number; maxSize: number } {
  return {
    size: gamutBoundaryCache.size,
    maxSize: 100 // Could be made configurable
  };
}

/**
 * Compare gamuts between two substrates
 */
export function compareSubstrateGamuts(
  substrate1: SubstrateProfile,
  substrate2: SubstrateProfile
): {
  volumeRatio: number;
  overlapPercentage: number;
  brightnessDifference: number;
  chromaDifference: number;
} {
  const boundary1 = calculateSubstrateGamutBoundary(substrate1);
  const boundary2 = calculateSubstrateGamutBoundary(substrate2);

  const volume1 = 
    (boundary1.labMax.l - boundary1.labMin.l) *
    (boundary1.labMax.a - boundary1.labMin.a) *
    (boundary1.labMax.b - boundary1.labMin.b);

  const volume2 = 
    (boundary2.labMax.l - boundary2.labMin.l) *
    (boundary2.labMax.a - boundary2.labMin.a) *
    (boundary2.labMax.b - boundary2.labMin.b);

  const volumeRatio = volume1 / volume2;

  // Calculate overlap (simplified)
  const overlapL = Math.min(boundary1.labMax.l, boundary2.labMax.l) - 
                   Math.max(boundary1.labMin.l, boundary2.labMin.l);
  const overlapA = Math.min(boundary1.labMax.a, boundary2.labMax.a) - 
                   Math.max(boundary1.labMin.a, boundary2.labMin.a);
  const overlapB = Math.min(boundary1.labMax.b, boundary2.labMax.b) - 
                   Math.max(boundary1.labMin.b, boundary2.labMin.b);

  const overlapVolume = Math.max(0, overlapL) * Math.max(0, overlapA) * Math.max(0, overlapB);
  const overlapPercentage = overlapVolume / Math.max(volume1, volume2) * 100;

  const brightnessDifference = Math.abs(substrate1.brightness - substrate2.brightness);
  
  const avgAbsorption1 = Object.values(substrate1.absorptionCoefficients)
    .reduce((sum, coeff) => sum + coeff, 0) / 4;
  const avgAbsorption2 = Object.values(substrate2.absorptionCoefficients)
    .reduce((sum, coeff) => sum + coeff, 0) / 4;
  const chromaDifference = Math.abs(avgAbsorption1 - avgAbsorption2) * 100;

  return {
    volumeRatio,
    overlapPercentage,
    brightnessDifference,
    chromaDifference
  };
}
